import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { RestoreFromTrash, DeleteForever, DeleteOutline } from "@mui/icons-material";
import {
  getAccountsList,
  getCustomersList,
  setAccountStatus,
  setCustomerStatus,
  permanentDeleteAccount,
  permanentDeleteCustomer,
} from "../firebase/dataHandler";
import { useToast } from "../context/ToastContext";

export default function Trash() {
  const { showToast } = useToast();
  const [tabIndex, setTabIndex] = useState(0);
  const [deletedAccounts, setDeletedAccounts] = useState([]);
  const [deletedCustomers, setDeletedCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialog state for permanent delete confirmation
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    type: null, // 'account' | 'customer'
    id: null,
    name: "",
  });

  const loadTrashData = () => {
    setLoading(true);
    Promise.all([getAccountsList(), getCustomersList()])
      .then(([accounts, customers]) => {
        if (accounts) {
          const inactiveAccs = Object.values(accounts).filter(
            (a) => a.isActive === false
          );
          setDeletedAccounts(inactiveAccs);
        } else {
          setDeletedAccounts([]);
        }

        if (customers) {
          const inactiveCusts = Object.values(customers).filter(
            (c) => c.isActive === false
          );
          setDeletedCustomers(inactiveCusts);
        } else {
          setDeletedCustomers([]);
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading trash data:", err);
        showToast("Failed to load trash items", "error");
        setLoading(false);
      });
  };

  useEffect(() => {
    loadTrashData();
  }, []);

  const handleRestoreAccount = (accountId) => {
    setAccountStatus(accountId, true)
      .then(() => {
        showToast("Account restored successfully", "success");
        loadTrashData();
      })
      .catch((err) => showToast(err.message || "Failed to restore account", "error"));
  };

  const handleRestoreCustomer = (customerId) => {
    setCustomerStatus(customerId, true)
      .then(() => {
        showToast("Customer restored successfully", "success");
        loadTrashData();
      })
      .catch((err) => showToast(err.message || "Failed to restore customer", "error"));
  };

  const handlePermanentDelete = () => {
    const { type, id } = confirmDialog;
    if (type === "account") {
      permanentDeleteAccount(id)
        .then(() => {
          showToast("Account permanently deleted", "success");
          setConfirmDialog({ open: false, type: null, id: null, name: "" });
          loadTrashData();
        })
        .catch((err) => showToast(err.message || "Failed to delete account", "error"));
    } else if (type === "customer") {
      permanentDeleteCustomer(id)
        .then(() => {
          showToast("Customer permanently deleted", "success");
          setConfirmDialog({ open: false, type: null, id: null, name: "" });
          loadTrashData();
        })
        .catch((err) => showToast(err.message || "Failed to delete customer", "error"));
    }
  };

  return (
    <div className="page-pad fade-in">
      {loading && <LinearProgress sx={{ mb: 2, height: 3, borderRadius: 1 }} />}
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, fontSize: 28, mb: 0.5, color: "var(--text-primary)" }}>
          Trash & Recycle Bin
        </Typography>
        <Typography variant="body2" sx={{ color: "var(--text-muted)", fontSize: 13 }}>
          Manage soft-deleted accounts and customers. Restore them anytime or remove them permanently.
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: "var(--border-subtle)", mb: 3 }}>
        <Tabs
          value={tabIndex}
          onChange={(e, v) => setTabIndex(v)}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontFamily: "Inter, sans-serif",
              fontSize: 14,
              color: "var(--text-secondary)",
              "&.Mui-selected": { color: "var(--accent-green)" },
            },
            "& .MuiTabs-indicator": { backgroundColor: "var(--accent-green)" },
          }}
        >
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span>Deleted Accounts</span>
                <Chip
                  label={deletedAccounts.length}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    backgroundColor: "rgba(239, 68, 68, 0.15)",
                    color: "#ef4444",
                  }}
                />
              </Box>
            }
          />
          <Tab
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span>Deleted Customers</span>
                <Chip
                  label={deletedCustomers.length}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    backgroundColor: "rgba(239, 68, 68, 0.15)",
                    color: "#ef4444",
                  }}
                />
              </Box>
            }
          />
        </Tabs>
      </Box>

      {/* Tab Panel 0: Accounts */}
      {tabIndex === 0 && (
        <Box>
          {deletedAccounts.length === 0 ? (
            <Paper
              sx={{
                p: 5,
                textAlign: "center",
                background: "var(--bg-card)",
                border: "1px solid var(--border-subtle)",
                borderRadius: 4,
              }}
            >
              <DeleteOutline sx={{ fontSize: 48, color: "var(--text-muted)", mb: 1, opacity: 0.5 }} />
              <Typography sx={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 16 }}>
                No deleted accounts in Trash
              </Typography>
              <Typography sx={{ color: "var(--text-muted)", fontSize: 13, mt: 0.5 }}>
                When accounts are deleted, they will appear here.
              </Typography>
            </Paper>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                background: "var(--bg-card)",
                border: "1px solid var(--border-subtle)",
                borderRadius: 4,
                boxShadow: "none",
              }}
            >
              <Table size="small">
                <TableHead sx={{ background: "var(--bg-elevated)" }}>
                  <TableRow>
                    <TableCell sx={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 12 }}>A/C ID</TableCell>
                    <TableCell sx={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 12 }}>Name</TableCell>
                    <TableCell sx={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 12 }}>Principal (₹)</TableCell>
                    <TableCell sx={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 12 }}>Interest</TableCell>
                    <TableCell sx={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 12 }}>Date</TableCell>
                    <TableCell align="right" sx={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 12 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deletedAccounts.map((acc) => (
                    <TableRow key={acc.accountId} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                      <TableCell sx={{ color: "var(--text-primary)", fontWeight: 600, fontSize: 13 }}>
                        {acc.accountId?.split("_")[0]}
                      </TableCell>
                      <TableCell sx={{ color: "var(--text-primary)", fontSize: 13 }}>{acc.name}</TableCell>
                      <TableCell sx={{ color: "var(--text-primary)", fontSize: 13 }}>
                        ₹{Number(acc.amount || 0).toLocaleString("en-IN")}
                      </TableCell>
                      <TableCell sx={{ color: "var(--text-secondary)", fontSize: 13 }}>
                        {acc.interestPercentage}% (₹{acc.interestAmount})
                      </TableCell>
                      <TableCell sx={{ color: "var(--text-muted)", fontSize: 12 }}>{acc.date}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            color="success"
                            startIcon={<RestoreFromTrash />}
                            onClick={() => handleRestoreAccount(acc.accountId)}
                            sx={{
                              borderColor: "rgba(21, 195, 88, 0.5)",
                              color: "var(--accent-green)",
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: 2,
                              fontSize: 12,
                            }}
                          >
                            Restore
                          </Button>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setConfirmDialog({
                                open: true,
                                type: "account",
                                id: acc.accountId,
                                name: acc.name,
                              })
                            }
                            sx={{
                              background: "rgba(239, 68, 68, 0.1)",
                              "&:hover": { background: "rgba(239, 68, 68, 0.2)" },
                            }}
                          >
                            <DeleteForever fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Tab Panel 1: Customers */}
      {tabIndex === 1 && (
        <Box>
          {deletedCustomers.length === 0 ? (
            <Paper
              sx={{
                p: 5,
                textAlign: "center",
                background: "var(--bg-card)",
                border: "1px solid var(--border-subtle)",
                borderRadius: 4,
              }}
            >
              <DeleteOutline sx={{ fontSize: 48, color: "var(--text-muted)", mb: 1, opacity: 0.5 }} />
              <Typography sx={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 16 }}>
                No deleted customers in Trash
              </Typography>
              <Typography sx={{ color: "var(--text-muted)", fontSize: 13, mt: 0.5 }}>
                When customers are deleted, they will appear here.
              </Typography>
            </Paper>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                background: "var(--bg-card)",
                border: "1px solid var(--border-subtle)",
                borderRadius: 4,
                boxShadow: "none",
              }}
            >
              <Table size="small">
                <TableHead sx={{ background: "var(--bg-elevated)" }}>
                  <TableRow>
                    <TableCell sx={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 12 }}>Customer ID</TableCell>
                    <TableCell sx={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 12 }}>Name</TableCell>
                    <TableCell sx={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 12 }}>Contact</TableCell>
                    <TableCell align="right" sx={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 12 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deletedCustomers.map((cust) => (
                    <TableRow key={cust.clientId} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                      <TableCell sx={{ color: "var(--text-primary)", fontWeight: 600, fontSize: 13 }}>
                        {cust.clientId?.split("_")[0]}
                      </TableCell>
                      <TableCell sx={{ color: "var(--text-primary)", fontSize: 13 }}>{cust.name}</TableCell>
                      <TableCell sx={{ color: "var(--text-secondary)", fontSize: 13 }}>{cust.contact || "N/A"}</TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            color="success"
                            startIcon={<RestoreFromTrash />}
                            onClick={() => handleRestoreCustomer(cust.clientId)}
                            sx={{
                              borderColor: "rgba(21, 195, 88, 0.5)",
                              color: "var(--accent-green)",
                              textTransform: "none",
                              fontWeight: 600,
                              borderRadius: 2,
                              fontSize: 12,
                            }}
                          >
                            Restore
                          </Button>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              setConfirmDialog({
                                open: true,
                                type: "customer",
                                id: cust.clientId,
                                name: cust.name,
                              })
                            }
                            sx={{
                              background: "rgba(239, 68, 68, 0.1)",
                              "&:hover": { background: "rgba(239, 68, 68, 0.2)" },
                            }}
                          >
                            <DeleteForever fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Permanent Delete Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: null, id: null, name: "" })}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: "var(--bg-card)",
            border: "1px solid var(--border-subtle)",
          },
        }}
      >
        <DialogTitle sx={{ color: "var(--text-primary)", fontWeight: 700 }}>
          Delete Permanently?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "var(--text-secondary)" }}>
            Are you sure you want to permanently delete <strong>{confirmDialog.name}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmDialog({ open: false, type: null, id: null, name: "" })}
            sx={{ color: "var(--text-muted)" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePermanentDelete}
            variant="contained"
            color="error"
            sx={{ fontWeight: 700, borderRadius: 2 }}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
